import { AxiosResponse } from 'axios';
import { BaseAPI } from 'apis/amazon';

// ============================================================================
// Types - Data Models
// ============================================================================

/**
 * Metric values for API limits
 * null means unlimited
 */
export type ApiLimitMetric = {
  totalApiTranscribed: number | null; // Total minutes transcribed
  apiCalls: number | null; // Total API calls
  totalComputeTime: number | null; // Total compute time in minutes
  dailyApiRate: number | null; // Daily API rate limit (requests/day)
};

/**
 * API Limits configuration
 */
export type ApiLimits = {
  hardLimits: ApiLimitMetric; // Hard limits (cannot be exceeded)
  softLimits: ApiLimitMetric; // Soft limits (warning thresholds)
  softLimitEnabled: boolean; // Whether soft limits are enabled
};

// ============================================================================
// Types - API Request/Response
// ============================================================================

/**
 * Standard API response wrapper from backend
 */
type ApiResponseData<T> = {
  href: string;
  data: T;
  message?: string;
};

/**
 * Response for GET/PUT /api-limits
 */
type ApiLimitsResponse = {
  data: ApiLimits;
  message?: string;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Transforms the API response to the service response format
 */
const transformApiLimitsResponse = (
  apiResponse: AxiosResponse<ApiResponseData<ApiLimits>>,
): AxiosResponse<ApiLimitsResponse> => {
  return {
    ...apiResponse,
    data: {
      data: apiResponse.data.data,
      message: apiResponse.data.message,
    },
  };
};

/**
 * Validates that soft limits are less than hard limits
 */
const validateLimits = (limits: ApiLimits): void => {
  if (!limits.softLimitEnabled) {
    return; // Skip validation if soft limits are disabled
  }

  const metrics: (keyof ApiLimitMetric)[] = [
    'totalApiTranscribed',
    'apiCalls',
    'totalComputeTime',
    'dailyApiRate',
  ];

  for (const metric of metrics) {
    const soft = limits.softLimits[metric];
    const hard = limits.hardLimits[metric];

    // Only validate if both values are set (not null)
    if (soft !== null && hard !== null && soft >= hard) {
      throw new Error(
        `Soft Limit must be less than the Hard Limit for ${metric}`,
      );
    }
  }
};

// ============================================================================
// Service
// ============================================================================

export const ApiLimitsService = {
  /**
   * Get current API limits configuration
   * GET /api-limits
   */
  getApiLimits: async (): Promise<AxiosResponse<ApiLimitsResponse>> => {
    try {
      const response = await BaseAPI.get<ApiResponseData<ApiLimits>>(
        'user/api-limits',
      );
      return transformApiLimitsResponse(response);
    } catch (error) {
      console.error('Error fetching API limits:', error);
      throw error;
    }
  },

  /**
   * Update API limits configuration
   * PUT /api-limits
   */
  saveApiLimits: async (
    limits: ApiLimits,
  ): Promise<AxiosResponse<ApiLimitsResponse>> => {
    // Validate limits before sending to API
    validateLimits(limits);

    try {
      const response = await BaseAPI.put<ApiResponseData<ApiLimits>>(
        'user/api-limits',
        limits,
      );
      return transformApiLimitsResponse(response);
    } catch (error) {
      console.error('Error saving API limits:', error);
      throw error;
    }
  },
};
